
/**
 * Module dependencies.
 */

import { downloadPdfFile } from 'src/core/utils/downloads';
import { fetchUserFavoritesPdf } from 'src/api/app/my-revigres/favorites/fetch-user-favorites-pdf';
import { ifProp, theme } from 'styled-tools';
import { media, units } from 'src/styles/utils';
import { myRevigresRoutes } from 'src/core/routes';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import { useAuth } from 'src/context/auth/context';
import { useTranslate } from 'src/core/utils/translator';
import { withSession } from 'src/core/utils/session';
import Button from 'src/components/core/buttons/button';
import ConfirmationModal from 'src/components/my-revigres/core/confirmation-modal';
import Container from 'src/components/core/layout/container';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ProductCard from 'src/components/cards/product-card';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useCallback, useState } from 'react';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import downloadIcon from 'src/assets/svg/download.svg';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import useUserFavorites from 'src/api/app/my-revigres/favorites/use-user-favorites';
import wishlistAddIcon from 'src/assets/svg/favorite.svg';

/**
 * `PageWrapper` styled component.
 */

const PageWrapper = styled.div<{ isLoading: boolean }>`
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(10)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0 ${units(18)};
  `}

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. header .'
      '. empty  .'
      '. list   .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `HeaderWrapper` styled component.
 */

const HeaderWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    align-items: center;
    flex-direction: row;
    grid-area: header;
    justify-content: space-between;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2).attrs({ as: 'h1' })`
  ${media.max('ms')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: max-content;
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${units(3)};
  text-align: left;
  width: ${units(39)};

  ${media.min('ms')`
    grid-area: empty;
    margin: ${units(7)} auto 0;
  `}
`;

/**
 * `StyledSvg` styled component.
 */

const StyledSvg = styled(Svg)`
  margin-bottom: ${units(3.5)};
`;

/**
 * `List` styled component.
 */

const List = styled.div<{ isLoading: boolean }>`
  display: grid;
  grid-column-gap: ${units(2.5)};
  grid-template-columns: 100%;

  ${media.min('xxs')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('sm')`
    grid-column-gap: ${units(5)};
  `}

  ${media.min('ms')`
    grid-area: list;
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    grid-column-gap: ${units(7.5)};
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('xl')`
    grid-column-gap: ${units(10)};
  `}

  ${ifProp('isLoading', css`
    pointer-events: none;
  `)}
`;

/**
 * `StyledProductCard` styled component.
 */

const StyledProductCard = styled(ProductCard)`
  margin-bottom: ${units(3)};

  ${media.min('ms')`
    margin-bottom: ${units(5)};
  `}

  ${media.min('md')`
    margin-bottom: ${units(7)};
  `}
`;

/**
 * `Favorites` page.
 */

const Favorites = (): ReactElement => {
  const { locale, translate } = useTranslate();
  const { token } = useAuth();
  const routeResolver = useLocalizedRoute();
  const [isOpenRemoveFavoriteModal, setIsOpenRemoveFavoriteModal] = useState<boolean>();
  const [isLoadingPdf, setLoadingPdf] = useState<boolean>();
  const {
    data: favorites,
    isLoading,
    isSuccess,
    refetch
  } = useUserFavorites({ token });

  const handleGetPdf = useCallback(() => {
    setLoadingPdf(true);

    fetchUserFavoritesPdf({ locale, token }).then(({ data }) => {
      setLoadingPdf(false);

      return downloadPdfFile(data, translate('my-revigres:dashboard.menuOptions.favorites'));
    });
  }, [locale, token, translate]);

  const handleConfirm = useCallback(() => {
    refetch();
    setIsOpenRemoveFavoriteModal(false);
  }, [refetch]);

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags pageTitle={translate('my-revigres:dashboard.menuOptions.favorites')} />

      <PageWrapper isLoading={isLoading}>
        <Loading active={isLoading} />

        <Container>
          <Grid>
            <HeaderWrapper>
              <Title>
                {translate('my-revigres:dashboard.menuOptions.favorites')}
              </Title>

              {!isEmpty(favorites) && (
                <Button
                  icon={downloadIcon}
                  isLoading={isLoadingPdf}
                  onClick={handleGetPdf}
                  variant={'outlined'}
                >
                  {translate('my-revigres:actions.download')}
                </Button>
              )}
            </HeaderWrapper>

            {isSuccess && (
              <>
                {isEmpty(favorites) && (
                  <Empty>
                    <StyledSvg
                      icon={wishlistAddIcon}
                      size={units(3.5)}
                    />

                    <Type.H6
                      as={'p'}
                      marginBottom={units(3)}
                      small
                    >
                      <RawHtml>
                        {translate('my-revigres:favorites.emptyList')}
                      </RawHtml>
                    </Type.H6>

                    <StyledButton
                      href={routeResolver('inspiration')}
                      locale={locale}
                    >
                      {translate('common:actions.getInspired')}
                    </StyledButton>
                  </Empty>
                )}

                <List isLoading={isLoading}>
                  {map(favorites, ({ id, product }) => (
                    <StyledProductCard
                      key={id}
                      onUpdateList={refetch}
                      url={routeTemplate(myRevigresRoutes.favorite, { id })}
                      {...product}
                    />
                  ))}
                </List>
              </>
            )}
          </Grid>
        </Container>

        <ConfirmationModal
          description={translate('my-revigres:favorites.removeFavorite.description')}
          isOpen={isOpenRemoveFavoriteModal}
          onClose={() => setIsOpenRemoveFavoriteModal(false)}
          onConfirm={handleConfirm}
          title={translate('my-revigres:favorites.removeFavorite.title')}
        />
      </PageWrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps = withSession();

/**
 * Export `Favorites` page.
 */

export default Favorites;
